import { Module, Commit, ActionTree, MutationTree } from 'vuex';
import { Design } from '@/api/typings/designs';
import { TemplateProductListDto } from '@/api/typings/templateProducts';
import { Item, TemplateProduct, TreeItem, TreeType } from '../../types';
import api from '@/api/baseApi';
import { ImportLayersElements, ModalImportMode } from '@/typings/layers/layers';
import { getCanvaDesigns } from '@/api/canva';
import { CanvaFileParserDesign } from '@/typings/parsers/externalFileParser';

export interface LayersImportState {
    designs: Design[];
    totalDesigns: number;
    templates: TemplateProductListDto[];
    template?: TemplateProduct;
    totalTemplates: number;
    selectedItem?: Item;
    selectedTreeItems: TreeItem[];
    parsedFileLayers?: ImportLayersElements;
    canvaDesigns: CanvaFileParserDesign;
    fileBuffer?: ArrayBuffer;
    totalCanvaDesigns: number;
}

const defaultState: LayersImportState = {
    designs: [],
    totalDesigns: 0,
    templates: [],
    template: undefined,
    totalTemplates: 0,
    selectedItem: undefined,
    selectedTreeItems: [],
    parsedFileLayers: undefined,
    canvaDesigns: {
        next: undefined,
        designs: []
    },
    fileBuffer: undefined,
    totalCanvaDesigns: 0
};

const mutations: MutationTree<LayersImportState> = {
    setCanvaDesigns: (
        state,
        data: {
            canvaDesigns: CanvaFileParserDesign;
            totalCanvaDesigns: number;
        }
    ) => {
        state.canvaDesigns = data.canvaDesigns;
        state.totalCanvaDesigns = data.totalCanvaDesigns;
    },
    appendCanvaDesigns: (
        state,
        data: {
            canvaDesigns: CanvaFileParserDesign;
            totalCanvaDesigns: number;
        }
    ) => {
        state.canvaDesigns.designs = [...state.canvaDesigns.designs, ...data.canvaDesigns.designs];
        state.canvaDesigns.next = data.canvaDesigns.next;
        state.totalCanvaDesigns += data.totalCanvaDesigns;
    },
    clearCanvaDesigns: (state) => {
        state.canvaDesigns = {
            next: undefined,
            designs: []
        };
        state.totalCanvaDesigns = 0;
    },
    changeParsedFileImportMode: (state, data: ModalImportMode) => {
        if (!state.parsedFileLayers) return;
        state.parsedFileLayers.mode = data;
    },
    setParsedFileLayers: (state, data) => {
        state.parsedFileLayers = data;
    },
    clearParsedFileLayers: (state) => {
        state.parsedFileLayers = undefined;
    },
    setDesigns: (
        state,
        data: {
            designs: Design[];
            totalCount: number;
        }
    ) => {
        state.designs = data.designs;
        state.totalDesigns = data.totalCount;
    },
    clearDesigns: (state) => {
        state.designs = [];
        state.totalDesigns = 0;
    },
    setTemplates: (
        state,
        data: {
            templates: TemplateProductListDto[];
            totalTemplates: number;
        }
    ) => {
        state.templates = data.templates;
        state.totalTemplates = data.totalTemplates;
    },
    setTemplate: (state, data: TemplateProduct) => {
        state.template = data;
    },
    clearTemplate: (state) => {
        state.template = undefined;
    },
    clearTemplates: (state) => {
        state.templates = [];
        state.totalTemplates = 0;
    },
    setSelectedItem: (state, data) => {
        state.selectedItem = data;
    },
    clearSelectedItem: (state) => {
        state.selectedItem = undefined;
    },
    clearSelectedTreeItems: (state) => {
        state.selectedTreeItems = [];
    },
    addSelectedTreeItem: (state, data) => {
        const index = state.selectedTreeItems?.findIndex((item) => {
            const hasUuid = [TreeType.CANVAS_ELEMENT, TreeType.PAGE_EPS, TreeType.HIDDEN].includes(data.type);
            return hasUuid ? item.uuid === data.uuid : item.id === data.id;
        });
        if (!(index !== undefined && index !== -1)) {
            state.selectedTreeItems.push(data);
        }
    },
    removeSelectedTreeItem: (state, data) => {
        const index = state.selectedTreeItems?.findIndex((item) => {
            const hasUuid = [TreeType.CANVAS_ELEMENT, TreeType.PAGE_EPS, TreeType.HIDDEN].includes(data.type);
            return hasUuid ? item.uuid === data.uuid : item.id === data.id;
        });
        if (index !== undefined && index !== -1) {
            state.selectedTreeItems.splice(index, 1);
        }
    },
    setFileBuffer: (state, data: ArrayBuffer) => {
        state.fileBuffer = data;
    }
};

const actions: ActionTree<LayersImportState, LayersImportState> = {
    async getCanvaDesigns({ commit }: { commit: Commit }, { token, continuation, search }) {
        try {
            const { designs, next } = await getCanvaDesigns(token, continuation, search);

            const data = {
                canvaDesigns: { designs, next },
                totalCanvaDesigns: designs.length
            };

            if (continuation) {
                commit('appendCanvaDesigns', data);
                return;
            }
            commit('setCanvaDesigns', data);
        } catch (error) {
            console.error(error);
            throw new Error('Failed to fetch canva designs');
        }
    },
    async getDesigns({ commit }: { commit: Commit }, params) {
        try {
            const response = await api.get('/Admin/Designs', {
                params
            });
            const data = await response.data;
            commit('setDesigns', data);
        } catch (error) {
            console.error(error);
            throw new Error('Failed to fetch designs');
        }
    },
    async getDesign({ commit }: { commit: Commit }, designId) {
        try {
            const response = await api.get(`/Admin/Designs/${designId}`);

            const designTemplateIds = response.data.designTemplateIds;

            if (!designTemplateIds || designTemplateIds.length === 0) {
                throw new Error('Design template IDs not found in response data');
            }

            const designTemplateId = designTemplateIds[0];

            const responseTemplateDesign = await api.get(`/Admin/DesignTemplates/${designTemplateId}`, {
                params: {
                    useListEPS: true
                }
            });

            commit('setTemplate', responseTemplateDesign.data);
        } catch (error) {
            console.error(error);
            throw new Error('Failed to fetch design');
        }
    },
    async getTemplates({ commit }: { commit: Commit }, params) {
        try {
            const [templates, totalTemplates] = await Promise.all([
                api.get('/Product/GetProducts', {
                    params
                }),
                api.get('/Product/GetProductsCount', {
                    params: {
                        searchString: params.searchString,
                        deleted: false
                    }
                })
            ]);
            commit('setTemplates', {
                templates: templates.data,
                totalTemplates: totalTemplates.data
            });
        } catch (error) {
            console.error(error);
            throw new Error('Failed to fetch templates');
        }
    },
    async getTemplate({ commit }: { commit: Commit }, templateId) {
        try {
            const response = await api.get(`/Product/`, {
                params: {
                    productId: templateId,
                    useListEPS: true
                }
            });
            commit('setTemplate', response.data);
        } catch (error) {
            console.error(error);
            throw new Error('Failed to fetch template');
        }
    },
    clearTemplate({ commit }: { commit: Commit }) {
        commit('clearTemplate');
    },
    setSelectedItem({ commit }: { commit: Commit }, data) {
        commit('setSelectedItem', data);
    },
    clearSelectedItem({ commit }: { commit: Commit }) {
        commit('clearSelectedItem');
    },
    clearSelectedTreeItems({ commit }: { commit: Commit }) {
        commit('clearSelectedTreeItems');
    },
    addSelectedTreeItem({ commit }: { commit: Commit }, data) {
        commit('addSelectedTreeItem', data);
    },
    removeSelectedTreeItem({ commit }: { commit: Commit }, data) {
        commit('removeSelectedTreeItem', data);
    },
    changeParsedFileImportMode({ commit }: { commit: Commit }, data: ModalImportMode) {
        commit('changeParsedFileImportMode', data);
    },
    setParsedFileLayers({ commit }: { commit: Commit }, data) {
        commit('setParsedFileLayers', data);
    },
    clearParsedFileLayers({ commit }: { commit: Commit }, data) {
        commit('clearParsedFileLayers', data);
    },
    setFileBuffer({ commit }: { commit: Commit }, data: ArrayBuffer) {
        commit('setFileBuffer', data);
    },
    clearFileBuffer({ commit }: { commit: Commit }) {
        commit('clearFileBuffer');
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const store: Module<LayersImportState, any> = {
    namespaced: true,
    state: defaultState,
    mutations: mutations,
    actions: actions
};

export default store;
